import { castToDateWithoutTime } from '../dateutil';
import { castToBoolean } from "../util";
import { syncWindowDefinitions, defaultSyncWindowDefinition } from '../syncWindowDefinitions';

class StaffUser {
  constructor(data) {
    this._initWithData(data);
  }

  _initWithData(data = null) {
    data = data || {};
    this.id = data.id || null;
    this.username = data.email || null;
    this.first_name = data.first_name || null;
    this.last_name = data.last_name || null;
    this.programs = data.programs || [];
    this.clients = data.clients || [];
    this.venues = data.venues || [];
    this.payroll = data.payroll || [];
    this.shift_segments = data.shift_segments || [];
    this.route_segments = data.claim_segments || [];
    this.all_traits = data.all_traits || [];
    this.calendar_feeds = data.calendar_feeds || [];
    this.assigned_trait_ids = data.assigned_trait_ids || [];
    this.all_certifications = data.all_certifications || [];
    this.assigned_certification_ids = data.assigned_certification_ids || [];
    this.assigned_certification_expirations = data.assigned_certification_expirations || [];
    this.use_mandatory_check_ins = data.use_mandatory_check_ins || false;
    this.use_apply_checkins_button = data.use_apply_checkins_button || false;
    this.use_claim_attestation = data.use_claim_attestation || false;
    this.venues_custom_search_fields = data.venues_custom_search_fields || [];
    this.maconomy_enabled = data.maconomy_enabled || false
    // Drives the merchandising activity dashboard: status-based buckets
    // (Rejected / Incomplete / Complete / Available) instead of date-based panels.
    this.merchandising_enabled = data.merchandising_enabled || false;

    // CORE1-8103: reason options for the staff-portal Non-Compliant modal
    // dropdown ([{id, name}]). Per-visit enable/current state rides on each
    // position (non_compliance_enabled / non_compliant).
    this.non_compliance_reasons = data.non_compliance_reasons || [];

    // Value returned from server
    let syncWindowName = data.data_sync_window_name || data.syncWindowName || null;
    if (!(syncWindowName in syncWindowDefinitions)) {
      syncWindowName = defaultSyncWindowDefinition.name;
    }
    this.syncWindowName = syncWindowName;

    this.partialRecaps = data.partialRecaps || [];
    this.partialClaims = data.partialClaims || [];

    this._claimsByEventId = {};
    this._enqueuedClaimsByEventId = {};

    this._claimsByDate = {};
    this._routeSegmentsByDate = {};
    this._shiftSegmentsByDate = {};

    this.allow_sms = data.allow_sms || 'f';
    this.allow_email = data.allow_email || 'f';

    this.staffing_app_auto_refresh_minutes = data.staffing_app_auto_refresh_minutes || 0;
    this.staff_app_resize_images = data.staff_app_resize_images ?? true;
    this.staff_app_resize_images_max_pixel = data.staff_app_resize_images_max_pixel || 1500;
    this.staff_app_resize_images_quality = data.staff_app_resize_images_quality || 0.9;
    this.last_login_time = data.last_login_time || null;

    this.notifications = [];
  }

  clearAllData() {
    this._initWithData();
  }

  set positions(positions) {
    this._positions = positions;
    let previous    = [];
    let current     = [];
    let upcoming    = [];

    for (let position of positions) {
      position.maconomy_enabled = this.maconomy_enabled
      if (!position.event.isCancelled()) {
        if (position.isPast) {
          previous.push(position);
        } else if (position.isCurrent) {
          current.push(position);
        } else if (position.isFuture) {
          upcoming.push(position);
        }
      }
    }

    this.previousPositions = previous.reverse();
    this.currentPositions  = current;
    this.upcomingPositions = upcoming;
  }

  get positions() {
    return this._positions;
  }

  set claims(claims) {
    this._claims = claims;
    this._updateClaimsEventIndex();
    this._updateClaimsDateIndex();
    this._updateRouteSegmentsByDate();
    this._updateShiftSegmentsByDate();
  }

  get claims() {
    return this._claims;
  }

  set events(events) {
    this._events = events;
  }

  get events() {
    return this._events;
  }

  set shiftSegments(segments) {
    this._shift_segments = segments;
  }

  get shiftSegments() {
    return this._shift_segments;
  }

  set claimSegments(segments) {
    this._route_segments = segments;
  }

  get claimSegments() {
    return this._route_segments;
  }

  set syncWindowDefinition(syncWindowDefinition) {
    this._syncWindowDefinition = syncWindowDefinition;
    this._syncWindowName = syncWindowDefinition && syncWindowDefinition.name;
  }

  get syncWindowDefinition() {
    return this._syncWindowDefinition;
  }

  set syncWindowName(name) {
    this._syncWindowName = name;
    this._syncWindowDefinition = syncWindowDefinitions[name];
  }

  get syncWindowName() {
    return this._syncWindowName;
  }

  getClaimsForEventId(eventId) {
    eventId = parseInt(eventId);
    return this._claimsByEventId[eventId] || [];
  }

  getPartialRecapsForEventId(eventId) {
    return this.partialRecaps.filter(r => parseInt(r.events_id) === eventId);
  }

  getEnqueuedClaimsForEventId(eventId) {
    eventId = parseInt(eventId);
    return this._enqueuedClaimsByEventId[eventId] || [];
  }

  getClaimsByDate(date) {
    date = this.parseDate(date);
    return this._claimsByDate[date] || [];
  }

  getRouteSegmentsUsedByDate(date) {
    this._updateRouteSegmentsByDate();
    date = this.parseDate(date);
    return this._routeSegmentsByDate[date] || [];
  }

  getShiftSegmentsUsedByDate(date, exclude_position_id) {
    this._updateShiftSegmentsByDate(exclude_position_id);
    date = this.parseDate(date);
    return this._shiftSegmentsByDate[date] || [];
  }

  parseDate(date) {
    return castToDateWithoutTime(date);
  }
  /*
    Is the user able to schedule an adhoc event based on his current program assignments?
    @return bool
   */
  adhocSchedulingAvailableToUser() {
    for (const program of this.programs) {
      if (castToBoolean(program.enable_adhoc)) {
        return true;
      }
    }
    return false;
  }

  _updateClaimsEventIndex() {
    let claimsByEventId = {};
    for (let claim of this.claims) {
      let eventId = parseInt(claim.events_id);
      if (!(eventId in claimsByEventId)) {
        claimsByEventId[eventId] = [];
      }
      claimsByEventId[eventId].push(claim);
    }
    this._claimsByEventId = claimsByEventId;
  }

  _updateClaimsDateIndex() {
    let claimsByDate = {};
    for (let claim of this.claims) {
      let claim_date = this.parseDate(claim.claim_date);
      if (!(claim_date in claimsByDate)) {
        claimsByDate[claim_date] = [];
      }
      claimsByDate[claim_date].push(claim);
    }
    this._claimsByDate = claimsByDate;
  }

  _updateShiftSegmentsByDate(exclude_position_id) {
    let shiftSegmentsByDate = {};
    if (!exclude_position_id) {
      exclude_position_id = null;
    }

    if (this.positions && exclude_position_id) {
      for (const position of this.positions) {
        if (position.primaryClaim && exclude_position_id && (parseInt(position.id) !== parseInt(exclude_position_id))) {
          if (position.shift_segments_id && parseInt(position.shift_segments_id) !== 2) {
            const claim_date = this.parseDate(position.start_date);
            if (!(claim_date in shiftSegmentsByDate)) {
              shiftSegmentsByDate[claim_date] = [];
            }
            shiftSegmentsByDate[claim_date].push(parseInt(position.shift_segments_id));
          }
        }
      }
    }
    this._shiftSegmentsByDate = shiftSegmentsByDate;
  }

  _updateRouteSegmentsByDate() {
    let segmentsByDate = {};
    if (this.claims){
      for (let claim of this.claims) {
        let claim_date = this.parseDate(claim.claim_date);
        if (!(claim_date in segmentsByDate)) {
          segmentsByDate[claim_date] = [];
        }
        if (claim.route_segments_id) {
          segmentsByDate[claim_date].push(parseInt(claim.route_segments_id));
        }
      }
    }
    this._routeSegmentsByDate = segmentsByDate;
  }

  addEnqueuedClaimForEventId(claim, eventId) {
    if (this._enqueuedClaimsByEventId[eventId] === undefined) {
      this._enqueuedClaimsByEventId[eventId] = [];
    }
    this._enqueuedClaimsByEventId[eventId].push(claim);
  }

  removeEnqueuedClaim(claim) {
    Object.keys(this._enqueuedClaimsByEventId).forEach(eventId => {
      const idx = this._enqueuedClaimsByEventId[eventId].indexOf(claim);
      if (idx >= 0) {
        this._enqueuedClaimsByEventId[eventId].splice(idx, 1);
      }
    });
  }

  removeAllEnqueuedClaims() {
    this._enqueuedClaimsByEventId = {};
  }
}

export default StaffUser;
